import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
} from '@angular/core';
import { UntypedFormArray } from '@angular/forms';


import * as _ from 'lodash';

import { TableEditableService } from './table-editable.service';
import { CollapseGroupComponent } from '../../collapse/collapse-group/collapse-group.component';
import { EditableDetailsIconComponent } from './editable-details-icon/editable-details-icon.component';
import {first, takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'table-editable',
  templateUrl: 'table-editable.component.html',
  styleUrls: ['table-editable.component.scss'],
  providers: [TableEditableService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableEditableComponent implements OnInit, OnDestroy, OnChanges {

  @Input() rows: any;

  @Input() formArray: UntypedFormArray;

  @Input() controls: boolean = true;

  @Output() removeRows: EventEmitter<any> = new EventEmitter<any>();

  @Output() removeRowsFinished: EventEmitter<any> = new EventEmitter<any>();

  @Output() edit: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  @Output() addedRows: EventEmitter<any> = new EventEmitter<any>();

  @ContentChildren(CollapseGroupComponent, {descendants: true})
  collapseGroups: QueryList<CollapseGroupComponent>;

  @ContentChildren(EditableDetailsIconComponent, {descendants: true})
  editableIcons: QueryList<EditableDetailsIconComponent>;

  isEditing: boolean = false;
  selectedRowsLength: number = 0;

  get selectedRows(): any {
    return this.editableService.selectedRowsValue;
  }

  private isComponentActive: boolean = true;

  constructor(
    private editableService: TableEditableService,
    protected ref: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.editableService.selectedRows
    .pipe(takeWhile(() => this.isComponentActive))
    .subscribe((rows: any[]) => {
      this.ref.markForCheck();
      this.selectedRowsLength = rows.length;
    });
  }

  ngOnChanges() {
    this.editableService.init(this.rows);
  }

  ngOnDestroy() {
    this.isComponentActive = false;
    this.editableService.reset();
  }

  // handlers
  onEditClick() {
    this._setEditing(true);
  }

  onDoneClick() {
    this._setEditing(false);
  }

  onRemoveClick() {
    this.removeSelected();
  }

  // public
  public toggleLastCollapse(callback?: () => void, delay: number = 100) {
    _.delay(() => {
      const item = this.collapseGroups.last;
      if (_.isFunction(callback)) {
        item.animationDone
        .pipe(first())
        .subscribe(() => {
          callback();
        });
      }
      this.toggleCollapseTableRow(item);
    }, delay);
  }

  public toggleLastCountCollapse(lastCount?: number, delay: number = 100) {
    _.delay(() => {
      const collapseGroupsArr = this.collapseGroups.toArray();
      _.forEach(collapseGroupsArr, (item, index) => {
        // if item is entered in lastCount items
        if (index >= collapseGroupsArr.length - lastCount) {
          // if it's callback and item is last
          if (index === collapseGroupsArr.length - 1) {
            item.animationDone
            .pipe(first())
            .subscribe(() => {
              this.addedRows.emit();
            });
          }
          this.toggleCollapseTableRow(item);
        }
      });
    }, delay);
  }

  public toggleItemCollapse(index: number, delay: number = 100) {
    const collapseGroupsArr = this.collapseGroups.toArray();
    _.delay(() => {
      this.toggleCollapseTableRow(collapseGroupsArr[index]);
    }, delay);
  }

  public startEdit() {
    this._setEditing(true);
  }

  public cancel() {
    this._setEditing(false);
  }

  public removeSelected() {
    const rows = this.editableService.selectedRowsValue;
    this.removeRows.emit(rows);
    this.editableService.removeSelectedRows(this.formArray);
    if (this.formArray) { this.formArray.markAsDirty(); }
    _.defer(() => this.removeRowsFinished.emit(rows));
  }

  public resetSelected() {
    this.editableIcons.forEach((item: EditableDetailsIconComponent) => {
      item.unselect();
    });
  }

  // private
  private _setEditing(value: boolean) {
    this.ref.markForCheck();
    this.isEditing = value;
    this.editableService.isEditing = value;
    this.edit.emit(value);
  }

  private toggleCollapseTableRow(row: any) {
    this.ref.markForCheck();
    row.toggle();
    row.toggleEditableDetailsIcon();
  }
}
